import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { buttonSizes } from "./componentAnatomyButtonSizes";
import { buttonVariantSpecs } from "./componentAnatomyButtonVariants";

// Mined from designSystem/Button/Button.module.scss, plus
// designSystem/Button/buttonCountChip.module.scss for the chip. The sheet splits
// into three layers and so does this spec: the .button base block below, the
// size axis in componentAnatomyButtonSizes, the colour layer in
// componentAnatomyButtonVariants.

const parts: readonly AnatomyPart[] = [
    {
        id: "background",
        label: "Container background",
        element: "background-color on .button, via --button-bg",
    },
    {
        id: "border",
        label: "Border",
        element: "1px border always reserved on .button, via --button-border",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .button, via --button-fg",
    },
    {
        id: "countChip",
        label: "Count chip",
        element: "[data-slot-box] chip rendered inside a right icon slot",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "box-shadow drawn by the shared focus-ring mixin",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    { id: "hover", label: "Hover", selector: ":not(:disabled):hover" },
    { id: "pressed", label: "Pressed", selector: ":not(:disabled):active" },
    {
        id: "focus",
        label: "Focus",
        selector: ":not(:disabled):focus-visible",
    },
    { id: "disabled", label: "Disabled", selector: ":disabled" },
];

// What `.button` itself declares, before any size or variant block runs.
const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "border", property: "border-width", token: "--border-width" },
    {
        partId: "border",
        property: "border-radius",
        token: "--radius-m",
        via: "--button-radius",
        note: "The .button default, which only .xs and .link.xs move off.",
    },
    {
        partId: "countChip",
        property: "background-color",
        token: "--bg-accent-subtle",
    },
    { partId: "countChip", property: "color", token: "--fg-accent" },
    {
        partId: "countChip",
        property: "border-radius",
        literal: "6px",
        note: "Chip geometry is off-token: 6px radius, 16px box, 10px text.",
    },
    { partId: "countChip", property: "font-size", literal: "10px" },
];

export const buttonAnatomy: ComponentAnatomySpec = {
    label: "Button",
    schematic: "button",
    rootSelector: ".button",
    variantAxisLabel: "Variant",
    parts,
    states,
    sharedBindings,
    variants: buttonVariantSpecs,
    sizes: buttonSizes,
};
